import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import WorldStatsFlagsShareCard from '../../components/WorldStatsFlagsShareCard/WorldStatsFlagsShareCard';
import { calculateStats } from '../../utils/profileStats';
import { WEB_APP_URL } from '../../utils/webApp';
import './ProfileLinkFallbackPage.css';

// Use production API when on localhost so profile fetch works in local dev
const API_BASE =
  process.env.REACT_APP_APP_BASE_URL ||
  (typeof window !== 'undefined' && window.location.origin.includes('localhost')
    ? 'https://app.voiatravel.com'
    : '');

const ProfileLinkFallbackPage = () => {
  const { userId } = useParams();
  const { state } = useLocation();
  const [profile, setProfile] = useState(() => ({
    displayName: state?.displayName ?? null,
    photoURL: state?.photoURL ?? null,
    username: state?.username ?? null,
    worldPercentage: state?.worldPercentage ?? null,
    countriesCount: state?.countriesCount ?? null,
    continentsCount: state?.continentsCount ?? null,
    countryCodes: state?.countryCodes ?? null,
  }));
  const [loading, setLoading] = useState(!state?.displayName && !state?.photoURL);
  const appStoreUrl = process.env.REACT_APP_APP_STORE_URL || 'https://apps.apple.com/pt/app/voia-travel/id6747190886?l=en-GB';
  const playStoreUrl = process.env.REACT_APP_PLAY_STORE_URL || 'https://play.google.com/store/apps/details?id=com.voia.travel.mobile';

  // Preload app logo so it appears sooner
  useEffect(() => {
    const href = `${process.env.PUBLIC_URL || ''}/og-preview.png`;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, []);

  useEffect(() => {
    if (state?.displayName != null || state?.photoURL != null) return;
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/profile/${encodeURIComponent(userId)}`)
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => {
        if (!cancelled) {
          setProfile({
            displayName: data.displayName || null,
            photoURL: data.photoURL || null,
            username: data.username || null,
            worldPercentage: data.worldPercentage ?? null,
            countriesCount: data.countriesCount ?? null,
            continentsCount: data.continentsCount ?? null,
            countryCodes: Array.isArray(data.countryCodes) ? data.countryCodes : null,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, state?.displayName, state?.photoURL]);

  const displayName = profile.displayName;
  const photoURL = profile.photoURL;

  // Derive world %, countries count, continents count from countryCodes (same logic as mobile share-world-stats)
  const derivedStats = useMemo(() => {
    if (Array.isArray(profile.countryCodes) && profile.countryCodes.length > 0) {
      return calculateStats(profile.countryCodes);
    }
    return null;
  }, [profile.countryCodes]);

  const worldPercentage = profile.worldPercentage ?? derivedStats?.worldPercentage ?? null;
  const countriesCount = profile.countriesCount ?? derivedStats?.countriesCount ?? null;
  const continentsCount = profile.continentsCount ?? derivedStats?.continentsCount ?? null;

  const subtitle = displayName
    ? `Join and start sharing your travel journey today.`
    : 'This link opens a profile on VOIA.';
  const ctaLine = (appStoreUrl || playStoreUrl)
    ? 'Install VOIA to view their profile'
    : null;

  const headingText = displayName ? `${displayName}’s Travel Journey` : 'Open in Voia';
  const usernameLine = profile.username ? `in VOIA Travel` : null;

  return (
    <div className="profile-fallback-container">
      <main className="profile-fallback-content">
        {loading ? (
          <p className="profile-fallback-description">Loading…</p>
        ) : (
          <>
            <header className="profile-fallback-header">
              {photoURL && (
                <div className="profile-fallback-avatar-wrap">
                  <img
                    src={photoURL}
                    alt={displayName ? `${displayName}` : 'Profile'}
                    className="profile-fallback-avatar"
                  />
                </div>
              )}
              <h1 className="profile-fallback-title">{headingText}</h1>
              {usernameLine && (
                <p className="profile-fallback-username">{usernameLine}</p>
              )}
            </header>

            <WorldStatsFlagsShareCard
              worldPercentage={worldPercentage}
              countriesCount={countriesCount}
              continentsCount={continentsCount}
              countryCodes={profile.countryCodes}
              card={false}
              showLogo={false}
              showUsername={false}
              compactFlags
            />

            <div className="profile-fallback-cta-block">
              <p className="profile-fallback-description">{subtitle}</p>
              {ctaLine && (
                <p className="profile-fallback-cta onlyTouch">{ctaLine}</p>
              )}
              <img
                src={`${process.env.PUBLIC_URL || ''}/og-preview.png`}
                alt="Voia"
                className="profile-fallback-og-preview"
              />
            </div>

            {/* Desktop: start your own map in the web app; phones get the stores. */}
            <div className="onlyDesktop" data-track-location="profile_page">
              <a href={WEB_APP_URL} className="webAppButton">
                Map your own travels
              </a>
              <p className="webAppOr">Or get the app</p>
            </div>

            <div className="profile-fallback-stores">
              <a
                href={appStoreUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="profile-fallback-store-link profile-fallback-store-link--app"
                aria-label="Download on the App Store"
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/assets/app_store_badge.svg`}
                  alt="Download on the App Store"
                  className="profile-fallback-store-badge"
                />
              </a>
              <a
                href={playStoreUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="profile-fallback-store-link profile-fallback-store-link--play"
                aria-label="Get it on Google Play"
              >
                <img
                  src={`${process.env.PUBLIC_URL || ''}/assets/google_play_badge.png`}
                  alt="Get it on Google Play"
                  className="profile-fallback-store-badge"
                />
              </a>
            </div>

          </>
        )}
      </main>
    </div>
  );
};

export default ProfileLinkFallbackPage;
